import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/auth";
import { CATEGORIES, eur } from "@/lib/constants";
import { ArrowUpRight } from "lucide-react";
import { EDITION_FORMULAS, editionConfiguratorHref } from "@/lib/editionFormulas";

const FAMILY_HERO = [
  {
    key: "creation",
    tag: "Parcours A",
    title: "Je veux créer un support",
    body: "Charbonier Studio réalise la création graphique à partir de votre idée.",
    rows: [
      { label: "Première face / premier volet", price: "39 €", highlight: true },
      { label: "Chaque face / volet supplémentaire", price: "+19 €" },
      { label: "Flyer recto-verso (2 faces)", price: "58 €" },
      { label: "Dépliant 2 plis recto-verso (6 volets)", price: "134 €" },
    ],
    cta: "Créer mon support",
    to: "/creer-mon-support",
  },
  {
    key: "preparation",
    tag: "Parcours B",
    title: "J'ai déjà mon projet",
    body: "Charbonier Studio prépare, adapte ou corrige votre visuel existant.",
    rows: [
      { label: "Rendre mon fichier conforme à l'imprimeur", price: "29 €", highlight: true },
      { label: "Planche PDF", price: "29 €" },
      { label: "Le mettre au bon format", price: "19 €" },
      { label: "Contrôle prépresse", price: "24 €" },
    ],
    cta: "Préparer mon projet",
    to: "/preparer-mon-projet",
  },
  {
    key: "edition",
    tag: "Parcours C",
    title: "Mise en page & édition",
    body: "Livres, livrets, brochures, magazines et revues — tarif calculé automatiquement selon le volume.",
    rows: [
      { label: "Livre essentiellement texte", price: "149 € + 1,25 €/1 000 car.", highlight: true },
      { label: "Livre structuré", price: "179 € + 1,50 €/1 000 car." },
      { label: "Livre illustré", price: "179 € + 1,50 €/1 000 car. + 7 €/page illustrée" },
      { label: "Magazine (maquette existante)", price: "18 € / page" },
      { label: "Nouvelle maquette + mise en page magazine", price: "199 € + 18 €/page" },
      { label: "Brochure / livret", price: "14 € / page" },
    ],
    cta: "Mise en page & édition",
    to: "/configurer/edition",
  },
];

function Tiers({ tiers }) {
  if (!tiers || !tiers.length) return null;
  return (
    <ul className="mt-3 space-y-1.5">
      {tiers.map((t, i) => (
        <li key={i} className={`flex items-center justify-between gap-3 text-[13px] ${t.highlight ? "font-medium text-black" : "text-neutral-600"}`}>
          <span className="flex items-center gap-2">
            {t.highlight && <span className="inline-block h-1.5 w-1.5 bg-[#0E7C90] shrink-0" />}
            <span>{t.label}</span>
          </span>
          <span className="font-mono whitespace-nowrap">{t.note ? t.note : `${t.price} €`}</span>
        </li>
      ))}
    </ul>
  );
}

export default function Tarifs() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    api.get("/services")
      .then((r) => { if (mounted) setServices(Array.isArray(r.data) ? r.data : []); })
      .catch((e) => { if (mounted) setError(e?.message || "Erreur de chargement des tarifs"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const grouped = CATEGORIES
    .map((c) => ({ cat: c, list: services.filter((s) => s.category === c.slug) }))
    .filter((g) => g.list.length > 0);

  return (
    <div data-testid="page-tarifs">
      <section className="cs-cream pt-20 pb-10 border-b border-black/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Tarifs</div>
          <h1 className="cs-h1 mt-4 text-5xl lg:text-7xl">Prix de départ,<br />transparents.</h1>
          <p className="mt-6 max-w-2xl text-neutral-700 cs-body text-[15px] sm:text-base">
            Les prix affichés correspondent aux prestations décrites. Lorsque votre projet nécessite davantage
            de travail, une estimation vous est proposée avant toute intervention supplémentaire.
          </p>
        </div>
      </section>

      {/* Deux grandes portes d'entrée */}
      <section className="cs-cream py-12 border-b border-black/10" data-testid="tarifs-families">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <div className="cs-eyebrow text-neutral-500">Trois parcours</div>
          <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">Choisissez votre situation.</h2>
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FAMILY_HERO.map((f) => (
              <Link key={f.key} to={f.to} className="group cs-card p-8 flex flex-col justify-between min-h-[360px]"
                data-testid={`family-card-${f.key}`}>
                <div>
                  <div className="cs-eyebrow text-[#0E7C90]">{f.tag}</div>
                  <h3 className="cs-h3 mt-3 text-2xl lg:text-3xl">{f.title}</h3>
                  <p className="mt-3 text-[15px] text-neutral-700 leading-relaxed">{f.body}</p>
                  <ul className="mt-6 divide-y divide-black/10">
                    {f.rows.map((r, i) => (
                      <li key={i} className={`flex justify-between py-2 text-[14px] ${r.highlight ? "text-black font-medium" : "text-neutral-700"}`}>
                        <span className="flex items-center gap-2">
                          {r.highlight && <span className="inline-block h-1.5 w-1.5 bg-[#0E7C90]" />}
                          {r.label}
                        </span>
                        <span className="font-mono">{r.price}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mt-6 pt-4 border-t border-black/15 flex items-center justify-between">
                  <span className="font-mono text-[13px] uppercase tracking-[0.14em] group-hover:text-[#0E7C90] transition-colors">{f.cta}</span>
                  <ArrowUpRight size={18} className="group-hover:text-[#0E7C90] transition-colors" />
                </div>
              </Link>
            ))}
          </div>
          <p className="mt-6 text-[13px] font-mono text-neutral-500 max-w-3xl">
            Les tarifs de préparation NE comprennent PAS la création graphique originale d'un visuel.
          </p>
        </div>
      </section>

      {loading && (
        <div className="cs-cream py-16 text-center font-mono text-sm text-neutral-500" data-testid="tarifs-loading">
          Chargement des tarifs…
        </div>
      )}

      {!loading && error && (
        <div className="cs-cream py-16 text-center text-sm text-neutral-700" data-testid="tarifs-error">
          {error}
        </div>
      )}

      {!loading && !error && grouped.length === 0 && (
        <div className="cs-cream py-16 text-center text-sm text-neutral-700" data-testid="tarifs-empty">
          Aucune prestation à afficher pour l'instant.
        </div>
      )}

      {!loading && !error && grouped.map(({ cat, list }, idx) => {
        const isLast = idx === grouped.length - 1;
        const sectionClass = `cs-cream py-12 ${isLast ? "" : "border-b border-black/10"}`;

        if (cat.slug === "edition") {
          return (
            <section
              key={cat.slug}
              className={sectionClass}
              data-testid={`tarifs-section-${cat.slug}`}
            >
              <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
                <div className="cs-eyebrow text-neutral-500">{cat.eyebrow}</div>
                <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">{cat.label}</h2>
                <p className="mt-3 max-w-2xl text-[15px] text-neutral-700">
                  Tarif calculé automatiquement selon le volume — plus aucune formule cachée.
                  Le configurateur reste la source de vérité pour le calcul exact.
                </p>
                <div className="mt-8 border-t border-black/15">
                  {EDITION_FORMULAS.map((f) => (
                    <Link
                      key={f.type}
                      to={editionConfiguratorHref(f.type)}
                      className="grid grid-cols-[1fr_auto] items-start border-b border-black/15 py-6 px-2 gap-6 hover:bg-white transition-colors"
                      data-testid={`tarif-edition-${f.type}`}
                    >
                      <div>
                        <div className="font-serif text-2xl">{f.name}</div>
                        <div className="mt-1 text-[14px] sm:text-sm text-neutral-600">{f.desc}</div>
                        <ul className="mt-3 space-y-1">
                          {f.formula.map((row, i) => (
                            <li
                              key={i}
                              className={`flex items-start gap-2 text-[14px] leading-snug ${
                                row.highlight ? "text-black font-medium" : "text-neutral-700"
                              }`}
                            >
                              {row.highlight && (
                                <span className="inline-block h-1.5 w-1.5 bg-[#0E7C90] mt-2 shrink-0" />
                              )}
                              <span className={row.highlight ? "" : "pl-3.5"}>{row.line}</span>
                            </li>
                          ))}
                        </ul>
                        {f.note && (
                          <div className="mt-2 text-[13px] font-mono text-neutral-500">{f.note}</div>
                        )}
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <div className="text-[11px] sm:text-xs font-mono tracking-[0.14em] uppercase text-neutral-500">
                          Configurateur
                        </div>
                        <div className="font-mono text-[13px] mt-1 group-hover:text-[#0E7C90]">Calculer&nbsp;→</div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          );
        }

        return (
          <section
            key={cat.slug}
            className={sectionClass}
            data-testid={`tarifs-section-${cat.slug}`}
          >
            <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
              <div className="cs-eyebrow text-neutral-500">{cat.eyebrow}</div>
              <h2 className="cs-h2 mt-3 text-3xl lg:text-4xl">{cat.label}</h2>
              <div className="mt-8 border-t border-black/15">
                {list.map((s) => (
                  <Link key={s.id} to={`/prestation/${s.slug}`}
                    className="grid grid-cols-[1fr_auto] items-start border-b border-black/15 py-6 px-2 gap-6 hover:bg-white transition-colors"
                    data-testid={`tarif-row-${s.slug}`}>
                    <div>
                      <div className="font-serif text-2xl">{s.name}</div>
                      <div className="mt-1 text-[14px] sm:text-sm text-neutral-600">{s.short_description}</div>
                      {Array.isArray(s.tiers) && s.tiers.length > 0 ? (
                        <Tiers tiers={s.tiers} />
                      ) : (
                        s.price_note ? <div className="mt-2 text-[13px] font-mono text-neutral-500">{s.price_note}</div> : null
                      )}
                    </div>
                    <div className="text-right whitespace-nowrap">
                      <div className="text-[11px] sm:text-xs font-mono tracking-[0.14em] uppercase text-neutral-500">À partir de</div>
                      <div className="font-serif text-3xl">{eur(s.starting_price)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        );
      })}
    </div>
  );
}
